import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
} from '@mui/material';
import { CloudUpload, Send, Home, CheckCircle, InsertDriveFile, Close } from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import hfssLogo from '../../assets/hfss_logo.png';

interface FormData {
  codice: string;
  adesione_nr: string;
  adesione_file: File | null;
  pagamento_file: File | null;
  iscrizione: boolean;
}

interface FilePreview {
  url: string;
  type: 'image' | 'pdf';
}

const InsuranceFormPage: React.FC = () => {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    codice: '',
    adesione_nr: '',
    adesione_file: null,
    pagamento_file: null,
    iscrizione: false,
  });

  const [adesionePreview, setAdesionePreview] = useState<FilePreview | null>(null);
  const [pagamentoPreview, setPagamentoPreview] = useState<FilePreview | null>(null);

  useEffect(() => {
    // If code is provided in URL, set it in the form
    const urlCode = code || searchParams.get('code');
    if (urlCode) {
      setFormData(prev => ({ ...prev, codice: urlCode.toUpperCase() }));
    }
  }, [code, searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'codice') {
      // Force uppercase and allow letters and numbers
      const upperValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      setFormData(prev => ({ ...prev, [name]: upperValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (fieldName: 'adesione_file' | 'pagamento_file') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({ ...prev, [fieldName]: file }));

      // Create preview
      const fileType = file.type;
      const previewUrl = URL.createObjectURL(file);
      const preview: FilePreview = {
        url: previewUrl,
        type: fileType === 'application/pdf' ? 'pdf' : 'image'
      };

      if (fieldName === 'adesione_file') {
        // Cleanup old preview URL
        if (adesionePreview) URL.revokeObjectURL(adesionePreview.url);
        setAdesionePreview(preview);
      } else {
        if (pagamentoPreview) URL.revokeObjectURL(pagamentoPreview.url);
        setPagamentoPreview(preview);
      }
    }
  };

  const handleRemoveFile = (fieldName: 'adesione_file' | 'pagamento_file') => {
    setFormData(prev => ({ ...prev, [fieldName]: null }));
    if (fieldName === 'adesione_file') {
      if (adesionePreview) URL.revokeObjectURL(adesionePreview.url);
      setAdesionePreview(null);
    } else {
      if (pagamentoPreview) URL.revokeObjectURL(pagamentoPreview.url);
      setPagamentoPreview(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form - accept codes between 8-12 characters
    if (!formData.codice || formData.codice.length < 8 || formData.codice.length > 12) {
      setError(t('insurance.errors.codeRequired'));
      return;
    }
    
    if (!formData.adesione_nr) {
      setError(t('insurance.errors.membershipNumberRequired'));
      return;
    }
    
    if (!formData.adesione_file || !formData.pagamento_file) {
      setError(t('insurance.errors.documentsRequired'));
      return;
    }
    
    if (!formData.iscrizione) {
      setError(t('insurance.errors.declarationsRequired'));
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const insuranceData = {
        codice: formData.codice,
        adesione_nr: parseInt(formData.adesione_nr),
        gdpr_ins: true,  // GDPR consent is implied by form submission
        iscrizione: formData.iscrizione
      };
      
      const data = new FormData();
      data.append('insurance_data', JSON.stringify(insuranceData));
      data.append('adesione_file', formData.adesione_file);
      data.append('pagamento_file', formData.pagamento_file);
      
      await api.post('/update-insurance', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSuccess(true);
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error('Submit error:', err);
      
      if (err.response?.data?.detail) {
        // Handle specific error messages from backend
        const detail = err.response.data.detail;
        if (detail.includes('not found')) {
          setError(t('insurance.errors.codeNotFound'));
        } else if (detail.includes('already submitted')) {
          setError(t('insurance.errors.alreadySubmitted'));
        } else {
          setError(detail);
        }
      } else {
        setError(t('insurance.errors.submitFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="static" sx={{ background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)' }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
              <Home />
            </IconButton>
            <Box component="img" src={hfssLogo} alt="HFSS" sx={{ height: 40, mr: 2, cursor: 'pointer' }}
                 onClick={() => navigate('/')} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('insurance.title')}</Typography>
          </Toolbar>
        </AppBar>
        
        <Box
          sx={{
            flexGrow: 1,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            py: 4,
          }}
        >
          <Container maxWidth="md">
            <Paper 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
              }}
            >
              <Typography variant="h4" sx={{ mb: 3, color: 'success.main', fontWeight: 700 }}>
                {t('insurance.success.title')}
              </Typography>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {t('insurance.success.message')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 4 }}>
                {t('insurance.success.emailConfirmation')}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/')}
                sx={{
                  background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
                  boxShadow: '0 3px 5px 2px rgba(51, 136, 255, .3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #0066ff 30%, #3388ff 90%)',
                  },
                }}
              >
                {t('insurance.backToHome')}
              </Button>
            </Paper>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" sx={{ background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <Home />
          </IconButton>
          <Box component="img" src={hfssLogo} alt="HFSS" sx={{ height: 40, mr: 2, cursor: 'pointer' }}
               onClick={() => navigate('/')} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('insurance.title')}</Typography>
        </Toolbar>
      </AppBar>
      
      <Box
        sx={{
          flexGrow: 1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: 4,
        }}
      >
        <Container maxWidth="md">
          <Paper 
            sx={{ 
              p: 4,
              borderRadius: 3,
              backdropFilter: 'blur(10px)',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 4, 
                textAlign: 'center', 
                fontWeight: 700,
                color: '#764ba2',
              }}
            >
              {t('insurance.title')}
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            <form onSubmit={handleSubmit}>
              <Box sx={{ mb: 4 }}>
                <TextField
                  fullWidth
                  required
                  label={t('insurance.code')}
                  name="codice"
                  value={formData.codice}
                  onChange={handleInputChange}
                  inputProps={{ 
                    maxLength: 12,
                    style: { textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600 }
                  }}
                  helperText={t('insurance.codeHelper')}
                  error={formData.codice.length > 0 && (formData.codice.length < 8 || formData.codice.length > 12)}
                  sx={{ mb: 3 }}
                />
                
                <TextField
                  fullWidth
                  required
                  type="number"
                  label={t('insurance.membershipNumber')}
                  name="adesione_nr"
                  value={formData.adesione_nr}
                  onChange={handleInputChange}
                  helperText={t('insurance.membershipNumberHelper')}
                />
              </Box>
              
              <Divider sx={{ my: 4 }} />
              
              <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                {t('insurance.requiredDocuments')}
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                {/* Adesione File Upload */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('insurance.membershipForm')}
                  </Typography>
                  {!formData.adesione_file ? (
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      startIcon={<CloudUpload />}
                      sx={{
                        height: '56px',
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                      }}
                    >
                      {t('insurance.selectFile')}
                      <input
                        hidden
                        accept=".jpg,.jpeg,.png,.pdf"
                        type="file"
                        onChange={handleFileChange('adesione_file')}
                      />
                    </Button>
                  ) : (
                    <Box sx={{
                      border: '2px solid',
                      borderColor: 'success.main',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'success.light',
                      opacity: 0.9
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Chip
                          icon={<CheckCircle />}
                          label={formData.adesione_file.name}
                          color="success"
                          sx={{ maxWidth: '80%' }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFile('adesione_file')}
                          sx={{ color: 'error.main' }}
                        >
                          <Close />
                        </IconButton>
                      </Box>
                      {adesionePreview && (
                        <Box sx={{ mt: 1, textAlign: 'center' }}>
                          {adesionePreview.type === 'image' ? (
                            <Box
                              component="img"
                              src={adesionePreview.url}
                              alt="Preview"
                              sx={{
                                maxWidth: '100%',
                                maxHeight: 200,
                                borderRadius: 1,
                                boxShadow: 2
                              }}
                            />
                          ) : (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              p: 2,
                              bgcolor: 'white',
                              borderRadius: 1
                            }}>
                              <InsertDriveFile color="error" sx={{ fontSize: 40 }} />
                              <Typography variant="body2">PDF {t('insurance.fileReady')}</Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Pagamento File Upload */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('insurance.paymentReceipt')}
                  </Typography>
                  {!formData.pagamento_file ? (
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      startIcon={<CloudUpload />}
                      sx={{
                        height: '56px',
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                      }}
                    >
                      {t('insurance.selectFile')}
                      <input
                        hidden
                        accept=".jpg,.jpeg,.png,.pdf"
                        type="file"
                        onChange={handleFileChange('pagamento_file')}
                      />
                    </Button>
                  ) : (
                    <Box sx={{
                      border: '2px solid',
                      borderColor: 'success.main',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'success.light',
                      opacity: 0.9
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Chip
                          icon={<CheckCircle />}
                          label={formData.pagamento_file.name}
                          color="success"
                          sx={{ maxWidth: '80%' }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFile('pagamento_file')}
                          sx={{ color: 'error.main' }}
                        >
                          <Close />
                        </IconButton>
                      </Box>
                      {pagamentoPreview && (
                        <Box sx={{ mt: 1, textAlign: 'center' }}>
                          {pagamentoPreview.type === 'image' ? (
                            <Box
                              component="img"
                              src={pagamentoPreview.url}
                              alt="Preview"
                              sx={{
                                maxWidth: '100%',
                                maxHeight: 200,
                                borderRadius: 1,
                                boxShadow: 2
                              }}
                            />
                          ) : (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              p: 2,
                              bgcolor: 'white',
                              borderRadius: 1
                            }}>
                              <InsertDriveFile color="error" sx={{ fontSize: 40 }} />
                              <Typography variant="body2">PDF {t('insurance.fileReady')}</Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
              
              <Divider sx={{ my: 4 }} />
              
              <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                {t('insurance.declarations')}
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="iscrizione"
                        checked={formData.iscrizione}
                        onChange={handleInputChange}
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {t('insurance.paymentConfirmation')} <span style={{ color: 'red' }}>*</span>
                      </Typography>
                    }
                    sx={{ alignItems: 'center' }}
                  />
                </Paper>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'center', 
                gap: 2 
              }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/')}
                  fullWidth={window.innerWidth < 600}
                  sx={{
                    minWidth: { xs: 'auto', sm: 150 },
                    borderColor: '#764ba2',
                    color: '#764ba2',
                    '&:hover': {
                      borderColor: '#5a3a82',
                      backgroundColor: 'rgba(118, 75, 162, 0.04)',
                    },
                  }}
                >
                  {t('insurance.backToHome')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  fullWidth={window.innerWidth < 600}
                  startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                  sx={{
                    minWidth: { xs: 'auto', sm: 200 },
                    background: 'linear-gradient(45deg, #ff00fb 30%, #ff4dff 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #cc00c8 30%, #ff00fb 90%)',
                    },
                    '&:disabled': {
                      background: 'rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  {loading ? t('insurance.submitting') : t('insurance.submit')}
                </Button>
              </Box>
            </form>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default InsuranceFormPage;